import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { StyledText } from 'components/Wizard/Global/WizardStyled';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, last } from 'lodash';
import Selector from 'components/Form/Selector';
import { isIterationSolution } from 'utils/SolutionHelpers';
import { kilometersToMeters } from 'utils/converter';
import Modal from '../../Presentation/Modal';
import { AMPLITUDE_EVENTS, AmplitudeService } from '../../../utils/amplitude';
import Button from '../../Form/Button';
import withTranslation from '../../../hoc/withTranslation';
import {
  StyledContrastPanel,
  StyledContrastPanelColumnsContainer,
  StyledContrastPanelFooter,
  StyledContrastPanelHeader,
  StyledContrastPanelHeaderText,
} from '../../Wizard/Global/WizardStyledContrastPanel';
import Section from '../../Form/Section';
import ObjectiveFunctions from './ObjectiveFunctions';
import TerminationCriteria from './TerminationCriteria';
import {
  clearVehicleParameter,
  setTourParameter,
  setUserParam,
  setSolutionJson,
} from '../../../actions';
import { StyledAccessContainer } from '../StyledComponents';
import entryPointIcon from '../../../global/img/settings.svg';
import { PANELS } from '../../../reducers/user';

import MultipleActionSetting from '../MapSettings/MultipleActionSetting';
import SingleActionSetting from '../MapSettings/SingleActionSetting';

export const OPTIONS = { General: 'general', Problem: 'problem', Solution: 'solution' };

export const MAP_SETTINGS = {
  [OPTIONS.General]: {
    SATELLITE_VIEW: 'satelliteView',
    SHOW_JOB_CLUSTERS: 'showJobClusters',
  },
  [OPTIONS.Problem]: {
    SHOW_JOB_CLUSTERS: 'showJobClusters',
    CONVERT_TO_TERRITORY: 'convertToTerritory',
    HIDE_POSITIONS: 'hidePositions',
    HIDE_GROUPS: 'hideGroups',
    HIDE_MAX_TIME_ON_VEHICLE: 'hideMaxTimeOnVehicle',
    HIDE_TERRITORIES: 'hideTerritories',
    HIDE_SKILLS: 'hideSkills',
    HIDE_PRIORITY: 'hidePriority',
    HIDE_PARKING: 'hideParking',
    HIDE_CATEGORY: 'hideCategory',
    ADD_VEHICLE_LIMIT: 'addVehicleLimit',
    SPEED_FACTOR: 'speedFactor',
    TERRITORIES: 'territories',
    SKILLS: 'skills',
    CATEGORIES: 'categories',
    TRAFFIC: 'traffic',
  },
  [OPTIONS.Solution]: {
    SHOW_STOPS: 'showStops',
    SHOW_CLUSTERS: 'showStopClusters',
    GRADIENT_ROUTES: 'gradientRoutes',
    SHOW_STRAIGHT_ROUTE_LINES: 'showStraightRouteLines',
    SHOW_TOUR_DIRECTION_ANIMATION: 'showTourDirectionAnimation',
    SHOW_TOUR_CONVEX_HULL: 'showTourConvexHull',
    SHOW_TOUR_CONCAVE_HULL: 'showTourConcaveHull',
    HIDE_ALL_ROUTES: 'hideAllRoutes',
    SHOW_INTERSECTIONS: 'showIntersections',
    SHOW_OVERLAPS: 'showOverlaps',
    HIDE_VISITED: 'hideVisited',
  },
};

export const TAB_OPTIONS = {
  Fleet: 'Fleet',
  Plan: 'Plan',
  Config: 'Config',
};

const PLAN_KEYS = [
  MAP_SETTINGS.problem.HIDE_POSITIONS,
  MAP_SETTINGS.problem.HIDE_GROUPS,
  MAP_SETTINGS.problem.HIDE_PRIORITY,
  MAP_SETTINGS.problem.HIDE_MAX_TIME_ON_VEHICLE,
  MAP_SETTINGS.problem.HIDE_PARKING,
];

const DEFAULT_TERRITORY_SKILLS_VALUE = 'a1,a2';

// Default values for vehicle limits (in user-facing units: km, hours, counts)
const DEFAULT_LIMIT_VALUES = {
  maxDistance: '100',
  shiftTime: '8',
  maxDrivingTime: '',
  maxStops: '',
  minStops: '',
};

// Default map settings for each tab
const DEFAULT_MAP_SETTINGS = {
  territories: { enabled: false, value: DEFAULT_TERRITORY_SKILLS_VALUE, userChange: false },
  skills: { enabled: false, value: DEFAULT_TERRITORY_SKILLS_VALUE, userChange: false },
  categories: { enabled: false, value: DEFAULT_TERRITORY_SKILLS_VALUE, userChange: false },
  speedFactor: { enabled: false, value: 2, userChange: false },
  traffic: { enabled: false, value: 'automatic', userChange: false },
  addVehicleLimit: { enabled: false, userChange: false },
  hidePositions: false,
  hideGroups: false,
  hidePriority: false,
  hideMaxTimeOnVehicle: false,
  hideParking: false,
};

const defaultLimitPlaceholders = {
  maxDistance: 'Enter distance in km',
  shiftTime: 'Enter shift time in hr',
  maxDrivingTime: 'Enter max time in hr',
  maxStops: 'Enter max stops',
  minStops: 'Enter min stops',
};

const tabs = Object.keys(TAB_OPTIONS).map((key) => ({ key: TAB_OPTIONS[key], value: key }));

const LimitFieldsWrapper = styled.div`
  padding-left: 2rem;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const LimitFieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: white;
`;

const Label = styled.label`
  width: 160px;
  flex-shrink: 0;
  font-size: 0.85rem; /* smaller font size */
`;

const Input = styled.input`
  background: #222;
  color: white;
  border: 1px solid #444;
  padding: 0.25rem 0.5rem;
  flex: 1;
  font-size: 0.85rem; /* match input font size */
`;

const StyledScrollableContent = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const GreyBox = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0.1rem 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.5);
  text-align: center;

  &:first-of-type {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-of-type {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    border-bottom: none;
  }
`;

const SolverConfigurationContainer = ({
  user,
  translations: { solverConfigLabels, mapSettingsPanel },
  wizardShown,
}) => {
  const solution = useSelector(({ solution: stateSolution }) => stateSolution);
  const tourPlanner = useSelector(
    ({ tourPlanner: stateTourPlanner }) => stateTourPlanner.value[solution.show],
  );
  const usageContext = useSelector(({ usageContext: stateUsageContext }) => stateUsageContext);

  const [showPanel, setShowPanel] = useState(false);
  const [objectiveFunctions, setObjectiveFunctions] = useState(
    cloneDeep(tourPlanner.solverConfiguration.objectiveFunctions),
  );
  const [terminationCriteria, setTerminationCriteria] = useState(
    cloneDeep(tourPlanner.solverConfiguration.terminationCriteria),
  );
  const [initialObjectiveFunctions, setInitialObjectiveFunctions] = useState(
    cloneDeep(tourPlanner.solverConfiguration.objectiveFunctions),
  );
  const [initialTerminationCriteria, setInitialTerminationCriteria] = useState(
    cloneDeep(tourPlanner.solverConfiguration.terminationCriteria),
  );
  const [selectedTab, setSelectedTab] = useState(TAB_OPTIONS.Fleet);

  const dispatch = useDispatch();
  const handleSetTourParameter = useCallback(
    (parameter) => dispatch(setTourParameter({ ...parameter, index: solution.show })),
    [dispatch, solution.show],
  );
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);
  const handleClearVehicleParameter = useCallback(
    (parameter) => dispatch(clearVehicleParameter(parameter, solution.show)),
    [dispatch, solution.show],
  );
  const handleSetSolutionJson = useCallback(
    (param) => dispatch(setSolutionJson(param)),
    [dispatch],
  );

  const showAccesContainer = !solution.json && ![3, 4].includes(usageContext.currentStep);

  const addVehicleLimitRef = useRef(null);
  const scrollableContentRef = useRef(null);
  const addVehicleLimitSwitcherRef = useRef();

  const [maxDistanceLimit, setMaxDistanceLimit] = useState(DEFAULT_LIMIT_VALUES.maxDistance);
  const [shiftTimeLimit, setShiftTimeLimit] = useState(DEFAULT_LIMIT_VALUES.shiftTime);
  const [maxDrivingTimeLimit, setMaxDrivingTimeLimit] = useState(
    DEFAULT_LIMIT_VALUES.maxDrivingTime,
  );
  const [maxStopsLimit, setMaxStopsLimit] = useState(DEFAULT_LIMIT_VALUES.maxStops);
  const [minStopsLimit, setMinStopsLimit] = useState(DEFAULT_LIMIT_VALUES.minStops);

  const limitState = {
    maxDistance: [maxDistanceLimit, setMaxDistanceLimit],
    shiftTime: [shiftTimeLimit, setShiftTimeLimit],
    maxDrivingTime: [maxDrivingTimeLimit, setMaxDrivingTimeLimit],
    maxStops: [maxStopsLimit, setMaxStopsLimit],
    minStops: [minStopsLimit, setMinStopsLimit],
  };

  const limitOptions = ['maxDistance', 'shiftTime', 'maxDrivingTime', 'maxStops', 'minStops'];
  const isSolution =
    solution && isIterationSolution(last(solution.requests[solution.show].iterations));

  const togglePanel = useCallback(
    (forceAction) => {
      const action = forceAction === undefined ? (showPanel ? 'close' : 'open') : forceAction;
      const info = { action };
      const newState = action === 'open';
      AmplitudeService.log(AMPLITUDE_EVENTS.SOLVER_CONFIG_ACTION, info);
      handleSetUserParam({ openPanel: newState ? PANELS.SOLVER_CONFIG : undefined });
      setShowPanel(newState);

      if (action === 'open') {
        // Capture initial objective functions and termination criteria for Config tab reset
        const initialObj = cloneDeep(tourPlanner.solverConfiguration.objectiveFunctions);
        const initialTerm = cloneDeep(tourPlanner.solverConfiguration.terminationCriteria);
        setObjectiveFunctions(initialObj);
        setTerminationCriteria(initialTerm);
        setInitialObjectiveFunctions(initialObj);
        setInitialTerminationCriteria(initialTerm);
      }
    },
    [setShowPanel, showPanel, tourPlanner, handleSetUserParam],
  );

  const handleOnChangeObjectiveFunctions = useCallback((newObjectiveFunctions) => {
    setObjectiveFunctions(newObjectiveFunctions);
  }, []);

  const handleOnChangeTerminationCriteria = useCallback(
    (newTerminationCriteria) => {
      setTerminationCriteria(newTerminationCriteria);
      handleSetTourParameter({
        solverConfiguration: { objectiveFunctions, terminationCriteria: newTerminationCriteria },
      });
    },
    [objectiveFunctions, handleSetTourParameter],
  );

  const handleOnSubmit = useCallback(() => {
    handleSetTourParameter({ solverConfiguration: { objectiveFunctions, terminationCriteria } });
    togglePanel();
  }, [objectiveFunctions, terminationCriteria, handleSetTourParameter, togglePanel]);

  const handleResetDefault = useCallback(() => {
    if (selectedTab === TAB_OPTIONS.Config) {
      // Reset Config tab: objective functions and termination criteria to initial state (when panel opened)
      setObjectiveFunctions(cloneDeep(initialObjectiveFunctions));
      setTerminationCriteria(cloneDeep(initialTerminationCriteria));
    } else if (selectedTab === TAB_OPTIONS.Fleet) {
      // Reset Fleet tab to DEFAULT constants (true factory defaults)

      // Reset map settings for Fleet tab using DEFAULT_MAP_SETTINGS
      const newMapSettings = { ...user.mapSettings };
      newMapSettings.territories = cloneDeep(DEFAULT_MAP_SETTINGS.territories);
      newMapSettings.skills = cloneDeep(DEFAULT_MAP_SETTINGS.skills);
      newMapSettings.categories = cloneDeep(DEFAULT_MAP_SETTINGS.categories);
      newMapSettings.speedFactor = cloneDeep(DEFAULT_MAP_SETTINGS.speedFactor);
      newMapSettings.traffic = cloneDeep(DEFAULT_MAP_SETTINGS.traffic);
      newMapSettings.addVehicleLimit = cloneDeep(DEFAULT_MAP_SETTINGS.addVehicleLimit);
      handleSetUserParam({ mapSettings: newMapSettings });

      // Reset limit input values to DEFAULT_LIMIT_VALUES
      setMaxDistanceLimit(DEFAULT_LIMIT_VALUES.maxDistance);
      setShiftTimeLimit(DEFAULT_LIMIT_VALUES.shiftTime);
      setMaxDrivingTimeLimit(DEFAULT_LIMIT_VALUES.maxDrivingTime);
      setMaxStopsLimit(DEFAULT_LIMIT_VALUES.maxStops);
      setMinStopsLimit(DEFAULT_LIMIT_VALUES.minStops);

      // Build new vehicle objects with defaults (remove speedFactor and traffic, reset limits)
      // for every vehicle type, instead of only the first one, so additional fleet types are
      // not silently dropped from the list.
      const newVehicles = tourPlanner.vehicles.map((vehicle) => {
        const { speedFactor: _sf, traffic: _tr, ...restVehicle } = vehicle;
        return {
          ...restVehicle,
          limits: {
            maxDistance: { enabled: false },
            shiftTime: { enabled: false },
            maxDrivingTime: { enabled: false },
            maxStops: { enabled: false },
            minStops: { enabled: false },
          },
        };
      });

      handleSetTourParameter({
        vehicles: newVehicles,
      });
    } else if (selectedTab === TAB_OPTIONS.Plan) {
      // Reset Plan tab to DEFAULT_MAP_SETTINGS
      const newMapSettings = { ...user.mapSettings };
      newMapSettings.hidePositions = DEFAULT_MAP_SETTINGS.hidePositions;
      newMapSettings.hideGroups = DEFAULT_MAP_SETTINGS.hideGroups;
      newMapSettings.hidePriority = DEFAULT_MAP_SETTINGS.hidePriority;
      newMapSettings.hideMaxTimeOnVehicle = DEFAULT_MAP_SETTINGS.hideMaxTimeOnVehicle;
      newMapSettings.hideParking = DEFAULT_MAP_SETTINGS.hideParking;
      handleSetUserParam({ mapSettings: newMapSettings });
    }
  }, [
    selectedTab,
    initialObjectiveFunctions,
    initialTerminationCriteria,
    handleSetUserParam,
    handleSetTourParameter,
    tourPlanner.vehicles,
    user.mapSettings,
  ]);

  const handleOnSwitchChange = useCallback(
    (id, value) => {
      const isConvertToTerritory = id === MAP_SETTINGS.problem.CONVERT_TO_TERRITORY;
      const isConcave = id === MAP_SETTINGS.solution.SHOW_TOUR_CONCAVE_HULL;
      const showStopsOff = id === MAP_SETTINGS.solution.SHOW_STOPS && !value;
      const showConvexHull = id === MAP_SETTINGS.solution.SHOW_TOUR_CONVEX_HULL && value;
      const showConcaveHull = isConcave && value;
      const isSpeedFactor = id === MAP_SETTINGS.problem.SPEED_FACTOR;
      const isTraffic = id === MAP_SETTINGS.problem.TRAFFIC;

      const isAddVehicleLimit = id === MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT;
      const isTerritories = id === MAP_SETTINGS.problem.TERRITORIES;
      const isSkills = id === MAP_SETTINGS.problem.SKILLS;
      const isCategories = id === MAP_SETTINGS.problem.CATEGORIES;
      const isHidePositions = id === MAP_SETTINGS.problem.HIDE_POSITIONS;
      const isHideGroups = id === MAP_SETTINGS.problem.HIDE_GROUPS;
      const isHidePriority = id === MAP_SETTINGS.problem.HIDE_PRIORITY;
      const isHideMaxTimeOnVehicle = id === MAP_SETTINGS.problem.HIDE_MAX_TIME_ON_VEHICLE;
      const isHideParking = id === MAP_SETTINGS.problem.HIDE_PARKING;

      let payload;

      if (isAddVehicleLimit) {
        payload = {
          enabled: value,
          userChange: true,
          value: {
            maxDistance: String(maxDistanceLimit),
            shiftTime: String(shiftTimeLimit),
            maxDrivingTime: String(maxDrivingTimeLimit),
            maxStops: String(maxStopsLimit),
            minStops: String(minStopsLimit),
          },
        };
      } else if (isConvertToTerritory) {
        payload = value ? 'enabled' : null;
      } else if (
        isConcave ||
        isSpeedFactor ||
        isTraffic ||
        isTerritories ||
        isSkills ||
        isCategories
      ) {
        payload = {
          ...user.mapSettings[id],
          enabled: value,
          userChange: true,
          ...(!user.mapSettings[id]?.value &&
            (isTerritories ? { value: DEFAULT_TERRITORY_SKILLS_VALUE } : {})),
          ...(!user.mapSettings[id]?.value &&
            (isSkills ? { value: DEFAULT_TERRITORY_SKILLS_VALUE } : {})),
          ...(!user.mapSettings[id]?.value &&
            (isCategories ? { value: DEFAULT_TERRITORY_SKILLS_VALUE } : {})),
          ...(!user.mapSettings[id]?.value &&
            (isTraffic
              ? { value: 'None' }
              : !isTerritories && !isSkills && !isCategories
              ? { value: 2 }
              : {})),
        };
      } else {
        payload = value;
      }

      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [id]: payload,
          ...(isAddVehicleLimit ? { isUserChange: true } : {}),
          ...(isHidePositions ? { positionsChange: true } : {}),
          ...(isHideGroups ? { groupsChange: true } : {}),
          ...(isHideMaxTimeOnVehicle ? { maxTimeChange: true } : {}),
          ...(isHidePriority ? { priorityChange: true } : {}),
          ...(isHideParking ? { parkingChange: true } : {}),
          ...(showStopsOff && { [MAP_SETTINGS.solution.SHOW_CLUSTERS]: false }),
          ...(showConvexHull && {
            [MAP_SETTINGS.solution.SHOW_TOUR_CONCAVE_HULL]: false,
          }),
          ...(showConcaveHull && {
            [MAP_SETTINGS.solution.SHOW_TOUR_CONVEX_HULL]: false,
          }),
        },
      });

      if (isSpeedFactor && payload.enabled) {
        handleSetTourParameter({
          vehicles: [
            {
              ...tourPlanner.vehicles[0],
              speedFactor: payload.value ?? 1,
            },
          ],
        });
      } else if (isSpeedFactor && !payload.enabled) {
        handleClearVehicleParameter(['speedFactor']);
      }

      if (isTraffic && payload.enabled) {
        handleSetTourParameter({
          vehicles: [
            {
              ...tourPlanner.vehicles[0],
              traffic: payload.value ?? 'automatic',
            },
          ],
        });
      } else if (isTraffic && !payload.enabled) {
        handleClearVehicleParameter(['traffic']);
      }

      if (isAddVehicleLimit) {
        addVehicleLimitSwitcherRef.current = value;

        if (value) {
          const newLimits = { ...tourPlanner.vehicles[0].limits };

          const toNum = (v) => (v === '' || v == null ? null : Number(v));

          const md = toNum(maxDistanceLimit);
          newLimits.maxDistance = md
            ? {
                enabled: true,
                value: kilometersToMeters(md),
                userChange: true,
              }
            : { enabled: false, value: null, userChange: true };

          const st = toNum(shiftTimeLimit);
          newLimits.shiftTime = st
            ? {
                enabled: true,
                value: st * 3600,
                userChange: true,
              }
            : { enabled: false, value: null, userChange: true };

          const mdt = toNum(maxDrivingTimeLimit);
          if (mdt) {
            newLimits.maxDrivingTime = {
              enabled: true,
              value: mdt * 3600,
              userChange: true,
            };
          } else {
            delete newLimits.maxDrivingTime;
          }

          const mxs = toNum(maxStopsLimit);
          if (mxs && mxs > 0) {
            newLimits.maxStops = {
              enabled: true,
              value: mxs,
              userChange: true,
            };
            newLimits.stops = {
              ...(newLimits.stops || {}),
              maxCount: { value: mxs },
              userChange: true,
              ...(newLimits.stops?.minCount ? { minCount: newLimits.stops.minCount } : {}),
            };
          } else {
            delete newLimits.maxStops;
            if (newLimits.stops) {
              delete newLimits.stops.maxCount;
              if (Object.keys(newLimits.stops).length === 1 && newLimits.stops.minCount == null) {
                delete newLimits.stops;
              }
            }
          }

          const mns = toNum(minStopsLimit);
          if (mns != null && mns >= 0) {
            newLimits.minStops = {
              enabled: true,
              value: mns,
              userChange: true,
            };
            newLimits.stops = {
              ...(newLimits.stops || {}),
              minCount: { value: mns },
              userChange: true,
              ...(newLimits.stops?.maxCount ? { maxCount: newLimits.stops.maxCount } : {}),
            };
          } else {
            delete newLimits.minStops;
            if (newLimits.stops) {
              delete newLimits.stops.minCount;
              if (Object.keys(newLimits.stops).length === 1 && newLimits.stops.maxCount == null) {
                delete newLimits.stops;
              }
            }
          }

          handleSetTourParameter({
            vehicles: [
              {
                ...tourPlanner.vehicles[0],
                ...(Object.keys(newLimits).length ? { limits: newLimits } : {}),
              },
            ],
          });
        } else {
          handleSetTourParameter({
            vehicles: [
              {
                ...tourPlanner.vehicles[0],
                limits: {
                  maxDistance: { enabled: false, value: '', userChange: true },
                  shiftTime: { enabled: false, value: '', userChange: true },
                  maxDrivingTime: { enabled: false, value: '', userChange: true },
                  maxStops: { enabled: false, value: '', userChange: true },
                  minStops: { enabled: false, value: '', userChange: true },
                },
              },
            ],
          });
        }
      }
    },
    [
      handleSetUserParam,
      user.mapSettings,
      tourPlanner,
      handleSetTourParameter,
      handleClearVehicleParameter,
      maxDistanceLimit,
      shiftTimeLimit,
      maxDrivingTimeLimit,
      maxStopsLimit,
      minStopsLimit,
    ],
  );

  const handleOnChangeInput = useCallback(
    (id, value) => {
      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [id]: { ...user.mapSettings[id], value, userChange: true, enabled: true },
        },
      });
      if (id === 'territories' || id === 'skills' || id === 'categories')
        handleSetSolutionJson({ jsonTriggerVRP: true });

      if (id === MAP_SETTINGS.problem.SPEED_FACTOR) {
        handleSetTourParameter({
          vehicles: [
            {
              ...tourPlanner.vehicles[0],
              speedFactor: value ?? 1,
            },
          ],
        });
      }
      if (id === MAP_SETTINGS.problem.TRAFFIC) {
        if (value === 'None') {
          handleClearVehicleParameter(['traffic']);
        } else {
          handleSetTourParameter({
            vehicles: [
              {
                ...tourPlanner.vehicles[0],
                traffic: value ?? 'automatic',
              },
            ],
          });
        }
      }
    },
    [
      user.mapSettings,
      handleSetUserParam,
      tourPlanner,
      handleSetTourParameter,
      handleSetSolutionJson,
    ],
  );

  const handleOnChangeLimitInput = useCallback(
    (fieldKey, rawValue) => {
      let value = rawValue;

      if (value === '') {
        switch (fieldKey) {
          case 'maxDistance':
            setMaxDistanceLimit('');
            break;
          case 'shiftTime':
            setShiftTimeLimit('');
            break;
          case 'maxDrivingTime':
            setMaxDrivingTimeLimit('');
            break;
          case 'maxStops':
            setMaxStopsLimit('');
            break;
          case 'minStops':
            setMinStopsLimit('');
            break;
          default:
            break;
        }

        const vehicle = tourPlanner.vehicles?.[0];
        const limits = { ...(vehicle?.limits || {}) };
        delete limits[fieldKey];

        if (fieldKey === 'maxStops' && limits.stops) {
          delete limits.stops.maxCount;
          if (Object.keys(limits.stops).length === 0) {
            delete limits.stops;
          }
        }
        if (fieldKey === 'minStops' && limits.stops) {
          delete limits.stops.minCount;
          if (Object.keys(limits.stops).length === 0) {
            delete limits.stops;
          }
        }

        handleSetTourParameter({
          vehicles: [
            {
              ...vehicle,
              limits,
            },
          ],
        });
        return;
      }

      if (
        ['maxDistance', 'shiftTime', 'maxDrivingTime'].includes(fieldKey) &&
        value &&
        parseFloat(value) < 0
      ) {
        value = '0';
      }
      if (['shiftTime', 'maxDrivingTime'].includes(fieldKey) && value && parseFloat(value) > 24) {
        value = '12';
      }
      if (fieldKey === 'maxStops' && value && parseInt(value, 10) < 1) {
        value = '1';
      }
      if (fieldKey === 'minStops' && value && parseInt(value, 10) < 0) {
        value = '0';
      }
      if (fieldKey === 'maxDistance' && value && parseFloat(value) > 3000) {
        value = '100';
      }

      switch (fieldKey) {
        case 'maxDistance':
          setMaxDistanceLimit(value);
          break;
        case 'shiftTime':
          setShiftTimeLimit(value);
          break;
        case 'maxDrivingTime':
          setMaxDrivingTimeLimit(value);
          break;
        case 'maxStops':
          setMaxStopsLimit(value);
          break;
        case 'minStops':
          setMinStopsLimit(value);
          break;
        default:
          break;
      }

      let convertedValue;
      if (fieldKey === 'maxDistance') {
        convertedValue = kilometersToMeters(parseFloat(value));
      } else if (fieldKey === 'shiftTime' || fieldKey === 'maxDrivingTime') {
        convertedValue = parseFloat(value) * 3600;
      } else if (fieldKey === 'maxStops' || fieldKey === 'minStops') {
        convertedValue = parseInt(value, 10);
      } else {
        convertedValue = value;
      }

      const vehicle = tourPlanner.vehicles?.[0];
      const limits = { ...(vehicle?.limits || {}) };
      limits[fieldKey] = {
        enabled: true,
        userChange: true,
        value: convertedValue,
      };

      if (fieldKey === 'maxStops') {
        limits.stops = {
          ...(limits.stops || {}),
          userChange: true,
          maxCount: { value: parseInt(value, 10) },
        };
      }
      if (fieldKey === 'minStops') {
        limits.stops = {
          ...(limits.stops || {}),
          userChange: true,
          minCount: { value: parseInt(value, 10) },
        };
      }

      handleSetTourParameter({
        vehicles: [
          {
            ...vehicle,
            limits,
          },
        ],
      });
    },
    [
      setMaxDistanceLimit,
      setShiftTimeLimit,
      setMaxDrivingTimeLimit,
      setMaxStopsLimit,
      setMinStopsLimit,
      tourPlanner,
      handleSetTourParameter,
    ],
  );

  useEffect(() => {
    const escFunction = (event) => {
      if (event.keyCode === 27 && showPanel) {
        togglePanel('close');
      }
    };

    if (showPanel) {
      document.addEventListener('keydown', escFunction, false);
      return () => {
        document.removeEventListener('keydown', escFunction, false);
      };
    }
    return undefined;
  }, [showPanel, togglePanel]);

  useEffect(() => {
    setObjectiveFunctions(tourPlanner.solverConfiguration.objectiveFunctions);
  }, [tourPlanner.solverConfiguration.objectiveFunctions]);

  useEffect(() => {
    setShowPanel(user.openPanel === PANELS.SOLVER_CONFIG);
  }, [user.openPanel, setShowPanel]);

  useEffect(() => {
    if (user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT] && addVehicleLimitRef.current) {
      addVehicleLimitRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      addVehicleLimitRef.current = null;
    }
  }, [user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT]]);

  useEffect(() => {
    const limits = tourPlanner.vehicles?.[0]?.limits || {};

    if (
      typeof limits.maxDistance?.value === 'number' &&
      !Number.isNaN(limits.maxDistance.value) &&
      limits.maxDistance.value !== 0
    ) {
      setMaxDistanceLimit(limits.maxDistance.value / 1000);
    } else {
      setMaxDistanceLimit(DEFAULT_LIMIT_VALUES.maxDistance);
    }

    if (
      typeof limits.shiftTime?.value === 'number' &&
      !Number.isNaN(limits.shiftTime.value) &&
      limits.shiftTime.value !== 0
    ) {
      setShiftTimeLimit(limits.shiftTime.value / 3600);
    } else {
      setShiftTimeLimit(DEFAULT_LIMIT_VALUES.shiftTime);
    }

    if (
      typeof limits.maxDrivingTime?.value === 'number' &&
      !Number.isNaN(limits.maxDrivingTime.value) &&
      limits.maxDrivingTime.value !== 0
    ) {
      setMaxDrivingTimeLimit(String(limits.maxDrivingTime.value / 3600));
    } else {
      setMaxDrivingTimeLimit(DEFAULT_LIMIT_VALUES.maxDrivingTime);
    }

    const maxStopsVal = limits.maxStops?.value ?? limits.stops?.maxCount?.value;
    if (typeof maxStopsVal === 'number' && !Number.isNaN(maxStopsVal) && maxStopsVal !== 0) {
      setMaxStopsLimit(maxStopsVal);
    } else {
      setMaxStopsLimit(DEFAULT_LIMIT_VALUES.maxStops);
    }

    const minStopsVal = limits.minStops?.value ?? limits.stops?.minCount?.value;
    if (typeof minStopsVal === 'number' && !Number.isNaN(minStopsVal) && minStopsVal !== 0) {
      setMinStopsLimit(minStopsVal);
    } else {
      setMinStopsLimit(DEFAULT_LIMIT_VALUES.minStops);
    }
  }, [
    tourPlanner.vehicles?.[0]?.limits?.maxDistance,
    tourPlanner.vehicles?.[0]?.limits?.shiftTime,
    tourPlanner.vehicles?.[0]?.limits?.maxDrivingTime,
    tourPlanner.vehicles?.[0]?.limits?.maxStops,
    tourPlanner.vehicles?.[0]?.limits?.minStops,
    tourPlanner.vehicles?.[0]?.limits?.stops,
  ]);

  useEffect(() => {
    const sfSetting = user.mapSettings[MAP_SETTINGS.problem.SPEED_FACTOR];
    const vehicleSF = tourPlanner.vehicles[0].speedFactor;

    if (
      sfSetting &&
      sfSetting.enabled &&
      sfSetting.value !== undefined &&
      sfSetting.value !== '' &&
      vehicleSF !== sfSetting.value
    ) {
      handleSetTourParameter({
        vehicles: [
          {
            ...tourPlanner.vehicles[0],
            speedFactor: sfSetting.value,
          },
        ],
      });
      return;
    }

    if (
      (!sfSetting || sfSetting.value === undefined) &&
      (vehicleSF === null || vehicleSF === undefined || vehicleSF === '')
    ) {
      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [MAP_SETTINGS.problem.SPEED_FACTOR]: {
            enabled: false,
            value: 2,
            userChange: false,
          },
        },
      });
    }
  }, [
    user.mapSettings[MAP_SETTINGS.problem.SPEED_FACTOR],
    tourPlanner.vehicles[0].speedFactor,
    handleSetUserParam,
    handleSetTourParameter,
    tourPlanner.vehicles,
    user.mapSettings,
  ]);

  useEffect(() => {
    const trafficSetting = user.mapSettings[MAP_SETTINGS.problem.TRAFFIC];
    const vehicleTraffic = tourPlanner.vehicles[0].traffic;

    if (
      trafficSetting &&
      trafficSetting.enabled &&
      trafficSetting.value &&
      vehicleTraffic !== trafficSetting.value
    ) {
      handleSetTourParameter({
        vehicles: [
          {
            ...tourPlanner.vehicles[0],
            traffic: trafficSetting.value,
          },
        ],
      });
      return;
    }

    if (
      (!trafficSetting || trafficSetting.value === undefined) &&
      (vehicleTraffic === null || vehicleTraffic === undefined || vehicleTraffic === '')
    ) {
      handleSetUserParam({
        mapSettings: {
          ...user.mapSettings,
          [MAP_SETTINGS.problem.TRAFFIC]: {
            enabled: false,
            value: 'automatic',
            userChange: false,
          },
        },
      });
    }
  }, [
    user.mapSettings[MAP_SETTINGS.problem.TRAFFIC],
    tourPlanner.vehicles[0].traffic,
    handleSetUserParam,
    handleSetTourParameter,
    tourPlanner.vehicles,
    user.mapSettings,
  ]);

  useEffect(() => {
    const trafficSetting = user.mapSettings[MAP_SETTINGS.problem.TRAFFIC];
    if (trafficSetting?.value === 'None' && trafficSetting?.enabled && !isSolution) {
      handleSetSolutionJson({ jsonTriggerVRP: true });
    }
  }, [user.mapSettings[MAP_SETTINGS.problem.TRAFFIC], isSolution, handleSetSolutionJson]);

  useEffect(() => {
    if (selectedTab === TAB_OPTIONS.Config && scrollableContentRef.current) {
      scrollableContentRef.current.scrollTop = 0;
    }
  }, [selectedTab]);

  useEffect(() => {
    const setting = user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT];
    if (setting === true) {
      addVehicleLimitSwitcherRef.current = true;
      return;
    }
    if (setting && typeof setting === 'object') {
      addVehicleLimitSwitcherRef.current = !!setting.enabled;
    }
  }, [user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT]]);

  return (
    <>
      <Modal isDark isVisible={showPanel} isFull fromTop>
        <StyledContrastPanel
          fullDisplay
          data-test-id="solverSettingsPanel"
          column
          style={{ paddingBottom: '50px' }}
        >
          <StyledContrastPanelHeader size="0">
            <StyledText bold contrast>
              {solverConfigLabels.configTitle}
            </StyledText>
            <StyledContrastPanelHeaderText paddingBottom>
              {solverConfigLabels.newConfigDesc}
            </StyledContrastPanelHeaderText>
          </StyledContrastPanelHeader>
          <StyledScrollableContent ref={scrollableContentRef} style={{ paddingBottom: '50px' }}>
            <Section extraPaddingTop>
              <Selector
                id="tab-select"
                initialValue={selectedTab}
                options={tabs}
                inverted
                onChange={setSelectedTab}
                contrast
              />
            </Section>
            {selectedTab === TAB_OPTIONS.Fleet && (
              <Section>
                <StyledContrastPanelColumnsContainer adjusted>
                  <MultipleActionSetting
                    mapSettingsPanel={mapSettingsPanel}
                    mapSettings={user.mapSettings}
                    setting={{ value: MAP_SETTINGS.problem.TERRITORIES }}
                    handleOnSwitchChange={handleOnSwitchChange}
                    handleOnChangeInput={handleOnChangeInput}
                    key={MAP_SETTINGS.problem.TERRITORIES}
                    noInfoIcon
                    useTableCheckbox
                  />
                  <MultipleActionSetting
                    mapSettingsPanel={mapSettingsPanel}
                    mapSettings={user.mapSettings}
                    setting={{ value: MAP_SETTINGS.problem.SKILLS }}
                    handleOnSwitchChange={handleOnSwitchChange}
                    handleOnChangeInput={handleOnChangeInput}
                    key={MAP_SETTINGS.problem.SKILLS}
                    noInfoIcon
                    useTableCheckbox
                  />
                  <MultipleActionSetting
                    mapSettingsPanel={mapSettingsPanel}
                    mapSettings={user.mapSettings}
                    setting={{ value: MAP_SETTINGS.problem.CATEGORIES }}
                    handleOnSwitchChange={handleOnSwitchChange}
                    handleOnChangeInput={handleOnChangeInput}
                    key={MAP_SETTINGS.problem.CATEGORIES}
                    noInfoIcon
                    useTableCheckbox
                  />
                  <MultipleActionSetting
                    mapSettingsPanel={mapSettingsPanel}
                    mapSettings={user.mapSettings}
                    setting={{ value: MAP_SETTINGS.problem.SPEED_FACTOR }}
                    handleOnSwitchChange={handleOnSwitchChange}
                    handleOnChangeInput={handleOnChangeInput}
                    key={MAP_SETTINGS.problem.SPEED_FACTOR}
                    noInfoIcon
                    useTableCheckbox
                  />
                  <MultipleActionSetting
                    mapSettingsPanel={mapSettingsPanel}
                    mapSettings={user.mapSettings}
                    setting={{ value: MAP_SETTINGS.problem.TRAFFIC }}
                    handleOnSwitchChange={handleOnSwitchChange}
                    handleOnChangeInput={handleOnChangeInput}
                    key={MAP_SETTINGS.problem.TRAFFIC}
                    noInfoIcon
                    useTableCheckbox
                  />
                  <SingleActionSetting
                    key={MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT}
                    mapSettings={user.mapSettings}
                    mapSettingsPanel={mapSettingsPanel}
                    setting={{ value: MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT }}
                    handleOnSwitchChange={handleOnSwitchChange}
                    useTableCheckbox
                  />
                </StyledContrastPanelColumnsContainer>
                {user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT] &&
                  user.mapSettings[MAP_SETTINGS.problem.ADD_VEHICLE_LIMIT]?.enabled !== false && (
                    <LimitFieldsWrapper ref={addVehicleLimitRef}>
                      {limitOptions.map((fieldKey) => (
                        <LimitFieldRow key={fieldKey}>
                          <Label>
                            {mapSettingsPanel[fieldKey] || fieldKey}
                            {fieldKey === 'maxDistance' ? ' (km)' : ''}
                            {['shiftTime', 'maxDrivingTime'].includes(fieldKey) ? ' (hr)' : ''}:
                          </Label>
                          <Input
                            min={fieldKey === 'maxStops' ? 1 : 0}
                            type="number"
                            value={
                              limitState[fieldKey][0] !== undefined
                                ? String(limitState[fieldKey][0])
                                : ''
                            }
                            onChange={(e) => handleOnChangeLimitInput(fieldKey, e.target.value)}
                            step={['maxDrivingTime', 'shiftTime'].includes(fieldKey) ? '0.1' : '1'}
                            inputMode={
                              ['maxDrivingTime', 'shiftTime'].includes(fieldKey)
                                ? 'decimal'
                                : 'numeric'
                            }
                            placeholder={defaultLimitPlaceholders[fieldKey] ?? ''}
                          />
                        </LimitFieldRow>
                      ))}
                    </LimitFieldsWrapper>
                  )}
              </Section>
            )}
            {selectedTab === TAB_OPTIONS.Plan && (
              <Section>
                <StyledContrastPanelColumnsContainer adjusted>
                  {PLAN_KEYS.map((key) => (
                    <SingleActionSetting
                      key={key}
                      mapSettings={user.mapSettings}
                      mapSettingsPanel={mapSettingsPanel}
                      setting={{ value: key }}
                      handleOnSwitchChange={handleOnSwitchChange}
                      useTableCheckbox
                    />
                  ))}
                </StyledContrastPanelColumnsContainer>
              </Section>
            )}
            {selectedTab === TAB_OPTIONS.Config && showPanel && (
              <Section>
                <GreyBox>
                  <TerminationCriteria
                    terminationCriteria={terminationCriteria}
                    onChange={handleOnChangeTerminationCriteria}
                  />
                </GreyBox>
                <GreyBox>
                  <ObjectiveFunctions
                    objectiveFunctions={objectiveFunctions}
                    onChange={handleOnChangeObjectiveFunctions}
                    terminationCriteriaEnabled={terminationCriteria.enabled}
                    solution={solution}
                  />
                </GreyBox>
              </Section>
            )}
          </StyledScrollableContent>
          <StyledContrastPanelFooter isDouble isFullWidth>
            <Button
              text={solverConfigLabels.buttonResetLabel}
              inverted
              contrast
              onClick={handleResetDefault}
              dataTestId="solverConfigResetBtn"
            />
            <div />
            <Button
              text={solverConfigLabels.buttonDoneLabel}
              onClick={handleOnSubmit}
              dataTestId="solverConfigDoneBtn"
            />
          </StyledContrastPanelFooter>
        </StyledContrastPanel>
      </Modal>
      {showAccesContainer && (
        <StyledAccessContainer
          data-testid="solverSettingBtn"
          id="solver-settings-entry-point"
          onClick={() => togglePanel()}
          title={solverConfigLabels.panelConfigurationTitle}
          order={2}
          icon={entryPointIcon}
          wizardShown={wizardShown}
        />
      )}
    </>
  );
};

export default withTranslation(SolverConfigurationContainer);
